import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Spinner,
} from '@fluentui/react-components';
import { DismissRegular, LinkRegular, AddRegular } from '@fluentui/react-icons';
import { Question, Tendering, TenderingPart } from '../../../../models/tendering';
import { deepCloneObject } from '../../../../utils/clone';
import { updateTenderingDocument, updateTenderingPartsByTenderingID } from '../../../../services/tenderingService';
import QuestionDialog from '../../QuestionDialog';

const GENERAL_QUESTIONS_PART = 'Allmänna frågor';

interface CreateTenderingPartsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  tendering: Tendering;
  onSaved: (updatedTendering: Tendering) => void;
  onError?: () => void;
}

const CreateTenderingPartsDialog: React.FC<CreateTenderingPartsDialogProps> = ({
  isOpen,
  onClose,
  tendering,
  onSaved,
  onError,
}) => {
  const [parts, setParts] = useState<TenderingPart[]>([]);
  const [newPartName, setNewPartName] = useState('');
  const [saving, setSaving] = useState(false);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [activePartID, setActivePartID] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      setParts(deepCloneObject(tendering.tenderingParts));
      setNewPartName('');
    }
  }, [isOpen, tendering]);

  const createNewTenderingPart = () => {
    if (!newPartName.trim()) return;

    const lastIndex = parts.length;
    const newPart: TenderingPart = {
      tenderingPartID: Number(parts[lastIndex - 1]?.tenderingPartID || 0) + 1,
      tenderingPart: newPartName.trim(),
      tenderingID: tendering.tenderingID,
      explanation: '',
      questions: [],
      evaluated: false,
      rankings: [],
    };

    setParts([...parts, newPart]);
    setNewPartName('');
  };

  const openQuestionPage = (partID: number) => {
    setActivePartID(partID);
    setQuestionDialogOpen(true);
  };

  const activePart = activePartID !== null ? parts.find(p => p.tenderingPartID === activePartID) : undefined;
  const generalQuestionsPart = parts.filter(p => p.tenderingPart === GENERAL_QUESTIONS_PART);

  const handleQuestionsSelected = (data: { selected: Question[] }) => {
    if (activePartID === null) {
      setQuestionDialogOpen(false);
      return;
    }

    setParts(prev => {
      const updated = deepCloneObject(prev) as TenderingPart[];
      const targetPart = updated.find(p => p.tenderingPartID === activePartID);
      const generalPart = updated.find(p => p.tenderingPart === GENERAL_QUESTIONS_PART);

      data.selected.forEach(question => {
        const exists = targetPart?.questions?.find(q => q.questionID === question.questionID);
        if (targetPart && !exists) {
          if (!targetPart.questions) targetPart.questions = [];
          targetPart.questions.push(question);

          if (generalPart?.questions) {
            generalPart.questions = generalPart.questions.filter(q => q.questionID !== question.questionID);
          }
        }
      });

      return updated;
    });

    setQuestionDialogOpen(false);
    setActivePartID(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedTendering = { ...tendering, tenderingParts: parts };
      await updateTenderingDocument(tendering.tenderingID, updatedTendering);

      const tenderingPartsWithoutQuestions = deepCloneObject(parts);
      tenderingPartsWithoutQuestions.forEach((part: any) => {
        delete part.questions;
      });
      await updateTenderingPartsByTenderingID(tendering.tenderingID, { tenderingParts: tenderingPartsWithoutQuestions });

      onSaved(updatedTendering);
    } catch (error) {
      console.error(error);
      onError?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog modalType='alert' open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
        <DialogSurface style={{ width: '600px', maxWidth: '600px' }}>
          <DialogBody>
            <DialogTitle
              action={
                <Button appearance="subtle" icon={<DismissRegular />} onClick={onClose} />
              }
            >
              Skapa nya delområde
            </DialogTitle>
            <DialogContent className="min-h-[300px]">
              {saving ? (
                <div className="flex items-center justify-center p-8">
                  <Spinner size="large" />
                </div>
              ) : (
                <div className="space-y-2">
                  {parts.map(part => (
                    <div key={part.tenderingPartID} className="flex items-center justify-between gap-2 p-2 border-b border-gray-200">
                      <h4 className="font-semibold">{part.tenderingPart}</h4>
                      {part.tenderingPart !== GENERAL_QUESTIONS_PART && (
                        <Button
                          icon={<LinkRegular />}
                          appearance="subtle"
                          title="Lägga till frågor"
                          onClick={() => openQuestionPage(part.tenderingPartID)}
                        />
                      )}
                    </div>
                  ))}

                  <div className="flex items-center gap-2 pt-2">
                    <Input
                      className="flex-1"
                      placeholder="Namn på nytt delområde"
                      value={newPartName}
                      onChange={(e) => setNewPartName(e.target.value)}
                    />
                    <Button
                      icon={<AddRegular />}
                      appearance="subtle"
                      title="Skapa ny delområde"
                      onClick={createNewTenderingPart}
                      disabled={!newPartName.trim()}
                    />
                  </div>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" disabled={saving} onClick={handleSave}>
                Spara
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {questionDialogOpen && activePart && (
        <QuestionDialog
          isOpen={questionDialogOpen}
          onClose={() => { setQuestionDialogOpen(false); setActivePartID(null); }}
          onSelect={handleQuestionsSelected}
          tenderingParts={generalQuestionsPart}
          singleSelect={false}
          selectedQuestions={activePart.questions?.map(q => ({ questionID: q.questionID }))}
        />
      )}
    </>
  );
};

export default CreateTenderingPartsDialog;
